import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../api";
import { formatDateTime } from "../../../shared/time";
import type { EventDetail } from "../../../shared/types";
import { Badge, Dialog, ErrorNote } from "../../ui";
import { useSession } from "../../shell/session";
import { EventForm } from "./EventForm";
import { PageFrame, hasRole, messageOf } from "./chrome";
import type { RegistrationRecord } from "../registrations/types";

export function EventPage() {
  const { eventId = "" } = useParams();
  const { user } = useSession();
  const canEdit = hasRole(user?.role, "coordinator");
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [registration, setRegistration] = useState<RegistrationRecord | null>(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setError("");
    void api<EventDetail>(`/api/v1/events/${eventId}`)
      .then((row) => { if (live) setEvent(row); })
      .catch((caught: unknown) => { if (live) { setEvent(null); setError(messageOf(caught)); } });
    void api<RegistrationRecord[]>("/api/v1/registrations")
      .then((rows) => { if (live) setRegistration(rows.find((row) => row.eventId === eventId) ?? null); })
      .catch(() => { if (live) setRegistration(null); });
    return () => { live = false; };
  }, [eventId]);

  async function setStatus(status: "cancelled" | "confirmed") {
    if (!event) return;
    setSaving(true);
    setError("");
    try {
      setEvent(await api<EventDetail>(`/api/v1/events/${event.id}`, { method: "PATCH", body: JSON.stringify({ status }) }));
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageFrame
      kicker="Event"
      title={event?.title ?? "Event"}
      lede={event ? `${formatDateTime(event.startsAt)} – ${formatDateTime(event.endsAt)}` : undefined}
      actions={<Link className="btn ghost" to="/calendar">Back to calendar</Link>}
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {!event && !error ? <p>Loading the room…</p> : null}
      {event && (
        <div className="grid-2">
          <article className="panel panel-pad stack">
            <div className="cluster">
              <Badge tone={event.status === "cancelled" ? "danger" : event.status === "draft" ? "amber" : "teal"}>{event.status}</Badge>
              <Badge tone="violet">{event.kind}</Badge>
              {event.capacity != null ? <Badge>{event.capacity} capacity</Badge> : <Badge>No capacity limit</Badge>}
            </div>
            <p>{event.place || "Place not set"}{event.roomName ? ` · ${event.roomName}` : ""}</p>
            <p className="prose muted">{event.description || "No description."}</p>
            <div>
              <h2>Resources</h2>
              {event.resources.length === 0 ? <p className="muted">None held.</p> : event.resources.map((resource) => (
                <p key={resource.id}>{resource.name} <span className="faint">{resource.kind}</span></p>
              ))}
            </div>
            <p className="faint">Cancelled events release the room and resources. There is no delete route, so cancel is the way to clear a booking. A restore fails if that room was taken in the meantime.</p>
            {canEdit ? (
              <div className="cluster">
                <button className="btn primary" type="button" onClick={() => setEditing(true)}>Edit event</button>
                {event.status === "cancelled"
                  ? <button className="btn" type="button" disabled={saving} onClick={() => void setStatus("confirmed")}>{saving ? "Saving…" : "Restore event"}</button>
                  : <button className="btn danger" type="button" disabled={saving} onClick={() => void setStatus("cancelled")}>{saving ? "Saving…" : "Cancel event"}</button>}
              </div>
            ) : <p className="notice">Editing needs a coordinator login.</p>}
          </article>
          <aside className="stack">
            <article className="panel panel-pad stack">
              <h2>Check-in</h2>
              <p className="muted">Search people, mark guests, and print a badge for this event.</p>
              <Link className="btn" to={`/check-in?eventId=${event.id}`}>Open check-in</Link>
            </article>
            <article className="panel panel-pad stack">
              <h2>Signup</h2>
              {registration ? (
                <>
                  <p className="muted">{registration.remaining} places left of {registration.capacity}. Public link {registration.published ? "is open" : "is not published"}.</p>
                  <Link className="btn" to={`/registrations/${registration.id}`}>Open signup desk</Link>
                  {registration.published ? <Link to={`/r/${registration.publicSlug}`}>Public page</Link> : null}
                </>
              ) : (
                <>
                  <p className="muted">No signup is attached. Create one from the event so capacity can be tracked.</p>
                  <Link className="btn" to={`/registrations?eventId=${event.id}`}>Create signup</Link>
                </>
              )}
            </article>
            {event.planId ? <article className="panel panel-pad stack"><h2>Plan</h2><Link to={`/services/${event.planId}`}>Open the linked plan</Link></article> : null}
          </aside>
        </div>
      )}
      {editing && event && (
        <Dialog title="Edit event" onClose={() => setEditing(false)}>
          <EventForm initial={event} onClose={() => setEditing(false)} onSaved={(saved) => { setEvent(saved); setEditing(false); }} />
        </Dialog>
      )}
    </PageFrame>
  );
}
